import { useState, useRef, useEffect } from "react"
import toast from "react-hot-toast"
import type { Equipment, EquipmentUnit } from "../hooks/useEquipments"

interface Props {
  equipment: Equipment
  canManage: boolean
  onUpdateUnits: (id: string, units: EquipmentUnit[]) => Promise<unknown>
}

// 호기 ID 생성
function genUnitId() {
  return `unit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

// 호기(시리얼번호 부여받은 개별 장비) 추가/관리 섹션
export function EquipmentUnitManager({ equipment, canManage, onUpdateUnits }: Props) {
  const [open, setOpen] = useState(false)
  const [serialNumber, setSerialNumber] = useState("")
  const [unitName, setUnitName] = useState("")
  const [saving, setSaving] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  const units = equipment.units || []

  // 외부 클릭 시 입력폼 닫기
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  const handleAdd = async () => {
    if (!unitName.trim()) {
      toast.error("장비 이름을 입력하세요.")
      return
    }
    setSaving(true)
    try {
      const next: EquipmentUnit[] = [
        ...units,
        {
          unitId: genUnitId(),
          serialNumber: serialNumber.trim(),
          unitName: unitName.trim(),
        },
      ]
      await onUpdateUnits(equipment._id, next)
      toast.success("호기가 추가되었습니다.")
      setSerialNumber("")
      setUnitName("")
      setOpen(false)
    } catch (error) {
      console.error("호기 추가 실패:", error)
      toast.error("호기 추가 중 오류가 발생했습니다.")
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (unitId: string) => {
    if (!window.confirm("해당 호기를 삭제하시겠습니까?")) return
    try {
      const next = units.filter((u) => u.unitId !== unitId)
      await onUpdateUnits(equipment._id, next)
      toast.success("호기가 삭제되었습니다.")
    } catch (error) {
      console.error("호기 삭제 실패:", error)
      toast.error("호기 삭제 중 오류가 발생했습니다.")
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">
          호기 목록 <span className="text-gray-400 font-normal">({units.length})</span>
        </h2>
        {canManage && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            시리얼번호 추가
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-1">
        시리얼번호를 부여받은 개별 호기를 등록하면, 이슈 등록 시 공통항목 또는 해당 호기를 선택할 수 있습니다.
      </p>

      {/* 호기 추가 폼 */}
      {open && canManage && (
        <div ref={formRef} className="mt-4 p-4 bg-orange-50/60 border border-orange-100 rounded-xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">시리얼번호</label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="예: SN-2024-001"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                장비 이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) handleAdd()
                }}
                placeholder="예: 1호기"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-white"
            >
              취소
            </button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 rounded-lg hover:from-orange-700 hover:to-amber-700 disabled:opacity-60"
            >
              {saving ? "추가 중..." : "추가"}
            </button>
          </div>
        </div>
      )}

      {/* 호기 목록 */}
      {units.length === 0 ? (
        <p className="text-sm text-gray-400 mt-4 py-4 text-center bg-gray-50 rounded-lg">
          등록된 호기가 없습니다. 이슈는 '공통항목'으로 등록됩니다.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100">
          {units.map((u) => (
            <li key={u.unitId} className="flex items-center justify-between py-2.5">
              <div className="min-w-0">
                <span className="font-medium text-gray-800">{u.unitName}</span>
                {u.serialNumber ? (
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    S/N {u.serialNumber}
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-gray-400">시리얼번호 미등록</span>
                )}
              </div>
              {canManage && (
                <button
                  onClick={() => handleRemove(u.unitId)}
                  className="text-xs text-red-400 hover:text-red-600 shrink-0"
                >
                  삭제
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
